import React, { useContext, useEffect, useRef } from 'react';
import DOMPurify from 'dompurify';
import store from '../../store/bookerStore';
import BookerContext from '../../context/bookerContext';
import BookerShortContext from '../../context/bookerShortContext';
import fieldParams from '../../const/fieldParams';
import CodeVerificationInput from './CodeVerificationInput';
import LocationInput from './LocationInput';
import PhoneNumberInput from './PhoneNumberInput';
import FirstLastNamesInput from './NamesInput';
import EmailInput from './EmailInput';
import PasswordInput from './PasswordInput';
import PaymentInput from './PaymentInput';
import NotesInput from './NotesInput';
import CountryInput from './CountryInput';
import CheckBox from './CheckBox';
import EstimatedFare from '../Booker/EstimatedFare';
import DownloadCurbApp from './DownloadCurbApp';
import PrivacyTerms from './PrivacyTerms';
import RideTypeSelect from './RideTypeSelect';
import RideTypeInput from './RideTypeInput';
import FleetsProvider from './FleetsProvider';
import DownloadCurbAppWeb from './DownloadCurbAppWeb';
import RideLater from './RideLater';
import LocationDisplay from './LocationDisplay';
import ToggleSMSNotification from './ToggleSMSNotification';

const FieldControl = ({ fieldKey, getNewFare }) => {
  const { key = fieldKey, preTitle, title, titleSub, helper, noteText, body } = fieldParams[fieldKey] || {};
  const context = useContext(BookerContext);
  const {
    state: { step, editor, accountCreation, isWebBooker },
    updateStep,
  } = context;
  const { phone: { formatted } = {} } = store.getState() || {};
  const { setIsWebBooker, togglePopup } = useContext(BookerShortContext);
  const formContainerRef = useRef(null);
  const helperObj = typeof helper === 'function' ? helper(accountCreation?.flow ?? (isWebBooker ? 'web' : '')) : helper;
  const { above: isHelperAbove, text: helperText } = helperObj || {};

  const note = typeof noteText === 'function' ? noteText(isWebBooker ? 'web' : '') : noteText;

  const InputCode = () => {
    return <CodeVerificationInput fieldKey={key} />;
  };
  const InputAddress = () => {
    return <LocationInput fieldKey={key} getNewFare={getNewFare} formContainerRef={formContainerRef} />;
  };
  const InputPhone = () => {
    return <PhoneNumberInput fieldKey={key} />;
  };
  const InputNames = () => {
    return <FirstLastNamesInput fieldKey={key} />;
  };
  const InputEmail = () => {
    return <EmailInput fieldKey={key} />;
  };
  const InputPassword = () => {
    return <PasswordInput fieldKey={key} />;
  };
  const InputPayment = () => {
    return <PaymentInput fieldKey={key} />;
  };
  const InputNotes = () => {
    return <NotesInput fieldKey={key} />;
  };
  const InputCountry = () => {
    return <CountryInput fieldKey={key} />;
  };
  const CheckBoxControl = () => {
    return <CheckBox fieldKey={key} />;
  };
  const SelectRideType = () => {
    return <RideTypeSelect fieldKey={key} />;
  };
  const InputRideType = () => {
    return <RideTypeInput fieldKey={key} />;
  };
  const Fleets = () => {
    return <FleetsProvider fieldKey={key} />;
  };

  const Input = ({ field }) => {
    switch (field) {
      case 'downloadApp':
        return <DownloadCurbApp />;
      case 'downloadAppWeb':
        return <DownloadCurbAppWeb />;
      case 'pickup':
      case 'dropoff':
        return <InputAddress />;
      case 'phone':
        return <InputPhone />;
      case 'code':
        return <InputCode />;
      case 'name':
        return <InputNames />;
      case 'email':
        return <InputEmail />;
      case 'password':
      case 'rePassword':
        return <InputPassword />;
      case 'payment':
        return <InputPayment />;
      case 'notes':
      case 'driverNote':
        return <InputNotes />;
      case 'country':
        return <InputCountry />;
      case 'accessibleVehicle':
      case 'termsAndConditions':
      case 'keepSignedIn':
        return <CheckBoxControl />;
      case 'rideType':
        return <SelectRideType />;
      case 'rideTypeInput':
        return <InputRideType />;
      case 'fleetsProvider':
        return <Fleets />;
      case 'rideLater':
        return <RideLater />;
      case 'pickupDisplay':
      case 'dropoffDisplay':
        return <LocationDisplay fieldKey={key} />;
      default:
        return <></>;
    }
  };
  const Title = ({ field }) => {
    if (editor) return <></>;

    let t = '';
    if (typeof title === 'string') t = title;
    if (typeof title === 'function') {
      t = title(accountCreation?.flow ?? (isWebBooker ? 'web' : ''));
      if (field === 'pickup' || field === 'dropoff') t = title(isWebBooker ? 'web' : '', step);
    }

    if (!t) return <></>;

    return <div className="title" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(t) }} />;
  };
  const PreTitle = () => {
    if (editor || !preTitle) return <></>;
    let t = '';
    if (typeof preTitle === 'function') t = preTitle(isWebBooker ? 'web' : '');
    else t = preTitle;

    if (!t) return <></>;
    return <div className="title-sub">{t}</div>;
  };
  const TitleSub = () => {
    if (editor || !titleSub) return <></>;
    let t = '';
    if (typeof titleSub === 'function') t = titleSub(accountCreation?.flow ?? (isWebBooker ? 'web' : ''));
    else t = titleSub;

    if (!t) return <></>;

    if (isWebBooker && step === 15) {
      return (
        <>
          <div className="title-sub">
            Sign up for text notifications to get important updates about rides you’ve requested.
          </div>
          <div className="title-sub title-sub-notification">
            <div className="sms-notification">{t}</div>
            <ToggleSMSNotification />
          </div>
        </>
      );
    }
    return (
      <div className="title-sub">
        {t}
        {key === 'code' && formatted && <span className="title-sub-phone"> {formatted}</span>}
      </div>
    );
  };

  const BodyText = () => {
    if (editor || !body) return <></>;
    return <div className="title-sub">{body}</div>;
  };

  if (key === 'map') return <></>;

  if (key === 'fare') return <EstimatedFare />;

  if (key === 'privacyTerms') return <PrivacyTerms fieldKey={key} />;

  return (
    <div
      className={`form-group-container group-container-${key}`}
      {...((key === 'pickup' || key === 'dropoff') && { ref: formContainerRef })}
    >
      {!editor && (
        <>
          <PreTitle />
          <Title field={key} />
          <TitleSub />
          <BodyText />
          {isHelperAbove && (
            <div className="title-helper helper-above">
              <span onClick={() => togglePopup(true, key)}>{helperText}</span>
            </div>
          )}
        </>
      )}
      <Input field={key} {...((key === 'pickup' || key === 'dropoff') && { formContainerRef: formContainerRef })} />
      {!editor && (
        <>
          {helper && helperText && !isHelperAbove && (
            <div className="title-helper" onClick={() => togglePopup(true, key)}>
              {helperText}
            </div>
          )}
          {note && <div className="title-note">{note}</div>}
        </>
      )}
    </div>
  );
};

export default FieldControl;
